import axios from 'axios';
import _orderBy from 'lodash.orderby';
import Vue from 'vue';
import zenscroll from 'zenscroll';
import Errors from '../classes/errors';
import {
    checkCCM19Consent,
    checkOneTrustConsent,
    getGaClientId,
    handleCloseDropdownOnBlur,
    handleLoadSharpsringScriptUrl,
    handleSetSharpspringScript,
    handleSharpspringSubmit,
    checkIsMooveOnDirectOrder,
    normalizeModelName,
    setCaptchaEuScript,
} from '../utils';
import { getFormRenderToken } from '../services/anti-bot';
import { isCaptchaEnabled } from '../services/captcha';

Vue.component('configurator-new-form-component', {
    props: {
        siteHandle: {
            value: String,
        },
        allStores: {
            value: Array,
        },
        imStores: {
            value: Array,
        },
        captchaEuPublicKey: {
            value: String,
        },
        sharpspringBaseUrl: {
            value: String,
        },
        sharpspringEndpointId: {
            value: String,
        },
        sharpspringScriptUrl: {
            value: String,
        },
    },

    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            formRenderToken: '',
            stores: this.allStores,
            modelName: '',
            offerTranslations: null,
            errors: new Errors(),
            formCompleted: false,
            submitting: false,
            countryHandle: '',

            editionName: '',
            editionVersion: '',

            dropdownHeardFrom: null,
            heardFrom: null,

            internalCountryHandle: '',
            dropdownCountry: null,

            guyane: 'GF',
            martinique: 'GF',
            mayotte: 'YT',
            reunion: 'RE',
            guadeloupe: 'GP',

            selectedBrandStore: null,

            salesforceInterestEdition: '',
            salesforceInterestVersion: '',

            isSwitchButtonVisible: true, // used only for germany configurator (corporate switch)
            requestIndex: 0, // used only for germany configurator (corporate switch)
            requestType: 'private', // used only for germany configurator (corporate switch)

            dropdownGender: null,
            gender: null,

            newsletterSignUp: false,
        };
    },

    computed: {
        orderedStores: function () {
            return _orderBy(this.stores, 'title');
        },

        isSubmitLoading() {
            return this.submitting ? 'button--loading' : '';
        },

        configurationJson() {
            const configuration = this.getConfigurationJson();

            this.modelName = this.$parent.data.generalTranslations.modelName;
            this.salesforceInterestEdition =
                this.$parent.data.edition.salesforceInterestEdition;
            this.salesforceInterestVersion =
                this.$parent.data.edition.salesforceInterestVersion;
            this.offerTranslations = this.$parent.data.offerTranslations;
            this.editionName = configuration.type.name ?? '';
            this.editionVersion = configuration.version.name ?? '';

            return JSON.stringify(configuration);
        },

        isCompanyInputVisible() {
            const countries = [
                'mgMotorsGermany',
                'mgMotorsFrance',
                'mgMotorsSpain',
                'mgMotorsEstonia',
                'mgMotorsPoland',
                'mgMotorsMacedonia',
                'mgMotorsMontenegro',
                'mgMotorsIreland',
            ];

            const countriesWithPrivateRequestCompany = ['mgMotorsIceland'];

            return (
                (this.requestType === 'business' &&
                    countries.includes(this.siteHandle)) ||
                countriesWithPrivateRequestCompany.includes(this.siteHandle)
            );
        },

        /**
         * Determines if this is a MooveOn direct order (Croatia + MooveOn payment).
         * When true, dealer selection is skipped and email goes only to MooveOn.
         */
        isMooveOnDirectOrder() {
            return checkIsMooveOnDirectOrder(
                this.siteHandle,
                this.$parent?.data?.edition?.paymentOption
                    ?.field_paymentOption_paymentHandle,
            );
        },
    },

    watch: {
        modelName(value) {
            // IM Specific brandstores are added only for Switzerland countries
            const switzerlandCountries = [
                'mgMotorsSwitzerlandIt',
                'mgMotorsSwitzerlandDe',
                'mgMotorsSwitzerlandFr',
            ];

            if (!switzerlandCountries.includes(this.siteHandle)) {
                this.stores = this.allStores;
                return;
            }

            if (value.toLowerCase().includes('im')) {
                this.stores = this.imStores;
            } else {
                this.stores = this.allStores;
            }
        },
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        if (this.siteHandle !== 'default') {
            this.countryHandle = this.siteHandle;
        }

        handleSetSharpspringScript(
            this.siteHandle,
            this.sharpspringBaseUrl,
            this.sharpspringEndpointId,
            'configuratorForm',
        );
        handleLoadSharpsringScriptUrl(
            this.siteHandle,
            this.sharpspringScriptUrl,
        );

        setCaptchaEuScript();

        getFormRenderToken().then((token) => {
            this.formRenderToken = token;
        });

        handleCloseDropdownOnBlur('#heard-from-dropdown');
        handleCloseDropdownOnBlur('#brandstore-dropdown');

        // Add keyboard accessibility for newsletter checkbox
        this.initializeNewsletterCheckboxAccessibility();
    },

    /**
     * Clean up event listeners when component is destroyed
     */
    beforeUnmount() {
        const label = document.querySelector(
            'label[for="checkbox-newsletter"]',
        );
        const checkbox = document.getElementById('checkbox-newsletter');

        if (label) {
            label.removeEventListener(
                'keydown',
                this.handleNewsletterCheckboxKeydown,
            );
        }
        if (checkbox) {
            checkbox.removeEventListener(
                'change',
                this.handleNewsletterCheckboxChange,
            );
        }
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        handleToggleButtonActiveClass(index) {
            return this.requestIndex === index ? 'checked' : '';
        },

        scrollToFirstError() {
            Vue.nextTick(() => {
                let firstError = document.querySelector(
                    '.new-input__container--error',
                );
                zenscroll.center(firstError);
            });
        },

        scrollToTop() {
            Vue.nextTick(() => {
                zenscroll.toY(0);
            });
        },

        setDropdownCountry(event) {
            this.toggleDropdown(event);

            this.dropdownCountry = event.currentTarget.dataset.text;
            this.countryHandle = event.currentTarget.dataset.value;
            this.internalCountryHandle = event.currentTarget.dataset.dorm;
        },

        toggleDropdown(event) {
            event.preventDefault();
            let element = event.currentTarget;
            while ((element = element.parentElement)) {
                if (element.classList.contains('dropdown')) {
                    element.classList.toggle('dropdown--active');
                }
            }
        },

        setDropdownHeardFrom(event) {
            this.dropdownHeardFrom = event.currentTarget.dataset.text;
            this.heardFrom = event.currentTarget.dataset.value;

            this.toggleDropdown(event);
        },

        brandStoreSelected(store) {
            this.selectedBrandStore = store;
        },

        async onSubmit(event) {
            event.preventDefault();
            const form = event.currentTarget;
            let dataFromForm = new FormData(form);
            let data = {};

            for (let [key, val] of dataFromForm.entries()) {
                Object.assign(data, { [key]: val });
            }

            if (
                this.siteHandle === 'mgMotorsFrance' &&
                this.agricoleDodDescription
            ) {
                data = {
                    ...data,
                    message: this.agricoleDodDescription,
                };
            }

            // Don't submit twice.
            if (this.submitting) {
                return;
            }

            this.submitting = true;

            try {
                if (!(await isCaptchaEnabled())) {
                    await this.handleDataSubmit();
                    return;
                }

                const captchaSolution = await KROT.getSolution(
                    this.captchaEuPublicKey,
                );

                await this.handleDataSubmit(captchaSolution);
            } catch (e) {
                throw new Error(e);
            } finally {
                this.submitting = false;
            }
        },

        getConfigurationJson() {
            const priceIndication = this.$parent.data.finance.priceText.replace(
                '{price}',
                this.$parent.data.totalPrice,
            );

            return {
                color: {
                    key: this.$parent.data.exterior.uniqueKey,
                    name: this.$parent.data.exterior.selectionTitle,
                    metallic: this.$parent.data.exterior.isMetallicColor
                        ? 1
                        : 0,
                    productCode: this.$parent.data.exterior.productCode,
                },
                type: {
                    id: this.$parent.data.entryId,
                    key: this.$parent.data.edition.uniqueKey,
                    name: this.$parent.data.edition.editionTitle,
                    productCode: this.$parent.data.edition.productCode,
                    // mg-api reads the drivetrain for BE/LU IM leads from this
                    // field. editionTitle above is translationMethod: site, so
                    // it cannot be matched on — this one is translationMethod:
                    // none and holds the same value on every market.
                    salesforceInterestEdition:
                        this.$parent.data.edition.salesforceInterestEdition,
                },
                options: {
                    hood: this.$parent.data.hood
                        ? {
                              key: this.$parent.data.hood.uniqueKey,
                              name: this.$parent.data.hood.selectionTitle,
                              productCode: this.$parent.data.hood.productCode,
                              type: this.$parent.data.subCollection,
                          }
                        : null,
                    interior: this.$parent.data.interior
                        ? {
                              key: this.$parent.data.interior.uniqueKey,
                              name: this.$parent.data.interior.selectionTitle,
                              productCode:
                                  this.$parent.data.interior.productCode,
                          }
                        : null,
                    interiorMaterial: this.$parent.data.interiorMaterial
                        ? {
                              key: this.$parent.data.interiorMaterial.uniqueKey,
                              name: this.$parent.data.interiorMaterial
                                  .selectionTitle,
                              productCode:
                                  this.$parent.data.interiorMaterial
                                      .productCode,
                          }
                        : null,
                },
                promotions: (() => {
                    const edition = this.$parent.data.edition;
                    const finance = this.$parent.data.finance;
                    const selectedKeys =
                        this.$parent.data.selectedPromotionKeys || [];
                    if (!edition || !edition.promotions) return [];
                    return edition.promotions
                        .filter(
                            (p) =>
                                p.financingHandle === finance.financingHandle,
                        )
                        .map((p) => ({
                            name: p.promotionTitle,
                            selected: selectedKeys.includes(
                                p.financingHandle + '::' + p.promotionTitle,
                            ),
                            discount: p.discount,
                        }));
                })(),
                version: ['mgMotorsCzech', 'mgMotorsSlovakia'].includes(
                    this.siteHandle,
                )
                    ? {
                          key:
                              this.$parent.data.edition
                                  .salesforceInterestVersion ?? null,
                          name: this.$parent.data.edition.editionTitle ?? null,
                      }
                    : {},
                price: {
                    key: this.$parent.data.finance.financingHandle,
                    name: this.$parent.data.finance.financingTitle,
                    shopPrice: null,
                    shopPriceAdditionMetallic: null,
                    shopPriceAdditionSpecificColor: null,
                },
                totalPriceIndication: priceIndication,
                priceIndication: priceIndication,
                ...((['mgMotorsCzech', 'mgMotorsSlovakia'].includes(this.siteHandle)) && {
                    vehiclePrice: this.$parent.data.totalPriceNumeric ?? null,
                }),
                disclaimer: '',
            };
        },

        async handleDataSubmit(captchaSolution) {
            try {
                const formData = new FormData(this.$refs.form);
                formData.append('sourceUrl', window.location.href);
                formData.append('gaClientId', getGaClientId());
                if (captchaSolution) {
                    formData.append(
                        'captchaSolution',
                        JSON.stringify(captchaSolution),
                    );
                }

                formData.append('formRenderToken', this.formRenderToken);
                const response = await axios.post('/', formData);
                const result = response.data;

                // Handle form errors.
                if (result.data.errors) {
                    this.errors.clear();
                    this.errors.record(result.data.errors);
                    this.submitting = false;
                    this.scrollToFirstError();
                    return;
                }
                this.sendGTMData();
                this.sendItalyGTMData(formData);
                handleSharpspringSubmit(
                    this.siteHandle,
                    this.sharpspringEndpointId,
                );

                window.location = result.data.redirectUrl;
            } catch (e) {
                throw new Error(e);
            }
        },

        sendGTMData() {
            const country =
                this.siteHandle == 'default'
                    ? 'mgMotorsEurope'
                    : this.siteHandle;

            const editionVersionAddition = this.editionVersion
                ? ` - ${this.editionVersion}`
                : '';
            const fullEditionName = `${this.editionName}${editionVersionAddition}`;

            if (checkCCM19Consent() || checkOneTrustConsent()) {
                // check if you are on production-enviroment
                if (!window.dataLayer) {
                    window.dataLayer = window.dataLayer || [];
                }

                const configurationData = {
                    event: 'configuration',
                    model: normalizeModelName(this.modelName, this.siteHandle),
                    edition: fullEditionName,
                    page_path: window.location.href,
                    country: `${country} - ${this.$root.languageCode}`,
                };

                if (this.siteHandle === 'mgMotorsPoland') {
                    const uniqueFormId = crypto.randomUUID();
                    configurationData.unique_form_id = uniqueFormId;
                }

                window.dataLayer.push(configurationData);
            }
        },

        sendItalyGTMData(formData) {
            if (this.siteHandle !== 'mgMotorsItaly') return;

            if (checkCCM19Consent() || checkOneTrustConsent()) {
                if (!window.dataLayer) {
                    window.dataLayer = window.dataLayer || [];
                }

                const storeData = JSON.parse(formData.get('store'));
                const italyDataLayerData = {
                    event: 'sent_form',
                    event_id: '003',
                    form_name: 'configurator',
                    car_model: normalizeModelName(
                        this.modelName,
                        this.siteHandle,
                    ),
                    city: storeData.city,
                };

                window.dataLayer.push(italyDataLayerData);
            }
        },

        /**
         * Initialize keyboard accessibility for newsletter checkbox
         */
        initializeNewsletterCheckboxAccessibility() {
            const checkbox = document.getElementById('checkbox-newsletter');
            const label = document.querySelector(
                'label[for="checkbox-newsletter"]',
            );

            if (!checkbox || !label) return;

            // Add accessibility attributes to the label
            label.setAttribute('tabindex', '0');
            label.setAttribute('role', 'button');
            label.setAttribute('aria-pressed', checkbox.checked.toString());

            // Add keyboard event listeners
            label.addEventListener(
                'keydown',
                this.handleNewsletterCheckboxKeydown,
            );
            checkbox.addEventListener(
                'change',
                this.handleNewsletterCheckboxChange,
            );
        },

        /**
         * Handle keyboard navigation for newsletter checkbox label
         */
        handleNewsletterCheckboxKeydown(event) {
            // Handle Space or Enter key on checkbox label
            if (event.key === ' ' || event.key === 'Enter') {
                event.preventDefault();
                const checkbox = document.getElementById('checkbox-newsletter');
                const label = document.querySelector(
                    'label[for="checkbox-newsletter"]',
                );

                if (checkbox && label) {
                    checkbox.checked = !checkbox.checked;
                    // Update aria-pressed state
                    label.setAttribute(
                        'aria-pressed',
                        checkbox.checked.toString(),
                    );
                    // Trigger change event for form handling
                    checkbox.dispatchEvent(new Event('change'));
                }
            }
        },

        /**
         * Handle checkbox state change to update ARIA attributes
         */
        handleNewsletterCheckboxChange(event) {
            this.newsletterSignUp = event.target.checked;
            const label = document.querySelector(
                'label[for="checkbox-newsletter"]',
            );
            if (label) {
                label.setAttribute(
                    'aria-pressed',
                    event.target.checked.toString(),
                );
            }
        },

        setDropdownGenderPicker(event) {
            this.dropdownGender = event.currentTarget.dataset.text;
            this.toggleDropdown(event);

            const value = event.currentTarget.dataset.value;
            this.gender = value;
        },
    },
});
